import { useCallback, useEffect, useRef, useState } from "react";

export type GallerySlide = {
  id: string;
  image?: string;
  caption?: string;
  /* The catalogue image is a transparent cut-out on no background and has to
     be contained and floated; a photograph fills the frame. Defaults to the
     photograph, because that is what a gallery normally holds. */
  fit?: "cover" | "contain";
  /* A slide can be drawn rather than photographed — the capacity diagrams
     ride in the same carousel as the photos so the two are one thing to
     swipe, not a diagram that disappears the moment a picture is uploaded. */
  node?: React.ReactNode;
};

type ClassGalleryProps = {
  slides: GallerySlide[];
  /** `light` sits on the page background; `dark` is the capacity carousel,
      whose caption bar reads over a photographed boot interior. */
  tone?: "light" | "dark";
  label: string;
  nextLabel: string;
  prevLabel?: string;
  placeholderLabel: string;
  placeholderIcon?: React.ReactNode;
  /* Lets the surrounding section follow the carousel — the capacity heading
     names whichever load is on screen. */
  onActiveChange?: (index: number) => void;
  /* For a gallery whose caption is printed outside the frame. */
  hideCaption?: boolean;
};

/* Distance from one slide to the next: the track's width on a phone, where
   each slide fills it, and a tile plus its gap where several share it. */
const slideStep = (track: HTMLElement) => {
  const first = track.children[0] as HTMLElement | undefined;
  if (!first) return track.clientWidth;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  return first.offsetWidth + gap;
};

/**
 * One-slide-per-view snap carousel with an overlaid caption bar and dots.
 *
 * The caption prints over a bottom gradient scrim inside the frame, and the
 * dots sit centred underneath it rather than on top of the picture.
 *
 * Scroll position drives the active dot, read from the track's own
 * scrollLeft. An IntersectionObserver did this once and was wrong twice over:
 * Chrome clips it to the viewport even with an element root, so the dots
 * stopped tracking when the gallery scrolled off screen, and it is suspended
 * entirely while the tab is not painting.
 *
 * A slide with no `image` renders the placeholder frame instead. That is the
 * expected state until the class galleries are uploaded — the carousel keeps
 * its full design (frame, caption, dots, arrow) either way.
 */
export default function ClassGallery({
  slides,
  tone = "light",
  label,
  nextLabel,
  prevLabel,
  placeholderLabel,
  placeholderIcon,
  onActiveChange,
  hideCaption,
}: ClassGalleryProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  /* The slide goTo is animating towards, while it animates. */
  const pendingRef = useRef<number | null>(null);
  const settleTimer = useRef<number>(0);
  const [active, setActive] = useState(0);
  /* Whether the track has anything left to scroll to. With one slide per
     view that is just "not the last slide"; on a tablet, where several sit
     side by side, the end arrives while the active slide is still short of
     the last. */
  const [canNext, setCanNext] = useState(true);

  /* Park on the first slide whenever the set changes — switching class, or
     switching the Luggage/Seating tab, should not inherit the previous
     gallery's scroll position. Written to scrollLeft rather than scrollTo so
     `html{scroll-behavior:smooth}` cannot animate it. */
  useEffect(() => {
    setActive(0);
    if (trackRef.current) trackRef.current.scrollLeft = 0;
  }, [slides]);

  /* Scroll position drives the active dot.
     This was an IntersectionObserver, which is the wrong instrument twice
     over: Chrome clips it to the viewport even when the root is an element,
     so the dots stopped tracking whenever the gallery scrolled off screen,
     and it is throttled entirely while the tab is not being painted. The
     track's own scrollLeft is the actual state being read, always available
     and exact. rAF keeps it off the scroll thread. */
  useEffect(() => {
    const track = trackRef.current;
    if (!track || slides.length < 2) return;

    const read = () => {
      const step = slideStep(track);
      if (!step) return;
      /* Math.abs: in RTL, Chrome reports scrollLeft as negative. */
      const scrolled = Math.abs(track.scrollLeft);
      setCanNext(scrolled + track.clientWidth < track.scrollWidth - 2);
      const index = Math.round(scrolled / step);
      const next = Math.max(0, Math.min(slides.length - 1, index));

      /* A smooth scroll emits a scroll event per frame, and rounding each
         intermediate position made the caption and the dots flip the moment
         the animation crossed the midpoint — then settle back. That flicker
         is the jitter on tapping forward. While goTo is animating, only the
         arrival counts; everything in between is ignored. */
      const pending = pendingRef.current;
      if (pending !== null) {
        if (next !== pending) return;
        pendingRef.current = null;
      }

      setActive(next);
      onActiveChange?.(next);
    };

    /* Read straight off the event rather than deferring to rAF. Both are
       cheap here — two properties the browser has already computed for the
       scroll — and rAF is paint-driven, so it is throttled to nothing
       whenever the tab is not being drawn, which left the dots stuck. */
    track.addEventListener("scroll", read, { passive: true });
    read();

    /* Whether anything is hidden depends on the width too, not only the
       scroll: rotate a tablet, resize the window, or let the section finish
       opening, and two or three tiles that overflowed now all fit. Read once
       on scroll alone, the next arrow kept the answer from the first layout
       and pointed at nothing. */
    const resize = new ResizeObserver(read);
    resize.observe(track);

    return () => {
      track.removeEventListener("scroll", read);
      resize.disconnect();
      window.clearTimeout(settleTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides]);

  /* Move the track on its own axis. scrollIntoView walks every scrollable
     ancestor and will drag the page vertically to reveal the slide, which on
     this screen means the whole step jumps while you are only paging a
     carousel. */
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    const card = track?.children[index] as HTMLElement | undefined;
    if (!track || !card) return;
    /* With several slides in view the track stops short of the last ones:
       aim at where it can actually go, and call that slide the target, or
       the arrival never matches and the carousel loses its place. */
    const offset = card.offsetLeft - track.offsetLeft;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const left = Math.sign(offset) * Math.min(Math.abs(offset), maxScroll);
    const step = slideStep(track);
    if (step) index = Math.round(Math.abs(left) / step);
    /* Claim the target before the animation starts, so the reads it
       generates are discarded until it arrives. */
    pendingRef.current = index;
    window.clearTimeout(settleTimer.current);
    /* Released on a timer too: a scroll that never completes — an
       interrupted animation, or a tab that is not being painted and so emits
       no scroll events at all — must not leave the carousel deaf. */
    settleTimer.current = window.setTimeout(() => {
      pendingRef.current = null;
    }, 700);

    track.scrollTo({ left, behavior: "smooth" });
    setActive(index);
    onActiveChange?.(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!slides.length) return null;

  const current = slides[Math.min(active, slides.length - 1)];
  const currentIsDrawn = Boolean(current?.node);
  const hasMultiple = slides.length > 1;

  return (
    <div className="class-gallery-wrap">
      <div
        className={`class-gallery class-gallery--${tone}${
          current?.image || currentIsDrawn ? "" : " is-placeholder"
        }${currentIsDrawn ? " is-drawn" : ""}`}
        role="group"
        aria-roledescription="carousel"
        aria-label={label}
      >
      <div className="class-gallery__track" ref={trackRef}>
        {slides.map((slide, index) => (
          <figure
            className={`class-gallery__slide${
              slide.node ? " is-drawn" : slide.image ? "" : " is-placeholder"
            }`}
            key={slide.id}
            data-index={index}
            aria-roledescription="slide"
            aria-label={`${index + 1} / ${slides.length}`}
          >
            {slide.node ? (
              slide.node
            ) : slide.image ? (
              <img
                loading="lazy"
                src={slide.image}
                alt={slide.caption ?? ""}
                className={
                  slide.fit === "contain" ? "is-cutout" : undefined
                }
              />
            ) : (
              <span className="class-gallery__placeholder">
                <i aria-hidden="true">{placeholderIcon}</i>
                <b>{placeholderLabel}</b>
              </span>
            )}
            {/* Each slide's own caption, for layouts that show several
                slides at once; hidden where the one shared caption below
                names the slide in view. Already the image's alt, so not
                read twice. */}
            {!hideCaption && slide.caption && (
              <figcaption className="class-gallery__tile-caption" aria-hidden="true">
                {slide.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>

      {/* Both directions, and neither wraps. The single next button looped
          silently from the last slide back to the first, so there was no way
          to step back one and no sign you had reached the end. Each arrow
          hides at its own end of the set. */}
      {hasMultiple && active > 0 && (
        <button
          type="button"
          className="class-gallery__nav class-gallery__prev"
          aria-label={prevLabel ?? "Previous"}
          onClick={() => goTo(active - 1)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M14.5 5.75 8.25 12l6.25 6.25"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {hasMultiple && active < slides.length - 1 && canNext && (
        <button
          type="button"
          className="class-gallery__nav class-gallery__next"
          aria-label={nextLabel}
          onClick={() => goTo(active + 1)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="m9.5 5.75 6.25 6.25-6.25 6.25"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {!hideCaption && current?.caption && (
        <p className="class-gallery__caption">{current.caption}</p>
      )}
      </div>

      {hasMultiple && (
        <div className="class-gallery__dots">
          {slides.map((slide, index) => (
            <button
              type="button"
              key={slide.id}
              className={index === active ? "is-active" : ""}
              aria-label={`${index + 1} / ${slides.length}`}
              aria-current={index === active}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
